import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAppSelector } from 'app/config/store';
import { colors, formatVND } from 'app/shared/theme/colors';
import { useTranslation } from 'app/shared/i18n';
import axios from 'axios';

const RANK_ICONS = ['👤', '🥉', '🥈', '🥇', '💎', '👑'];
const RANK_COLORS = ['#94A3B8', '#CD7F32', '#C0C0C0', '#FFD700', '#8B5CF6', '#F59E0B'];

export const DepositRankWidget = () => {
  const { t } = useTranslation();
  const [ranks, setRanks] = useState<any[]>([]);
  const account = useAppSelector(state => state.authentication.account);
  const currentRank = account?.rank;

  useEffect(() => {
    axios
      .get('/api/ranks')
      .then(res => {
        const sorted = (res.data || []).sort((a, b) => (a.minAmount || 0) - (b.minAmount || 0));
        setRanks(sorted);
      })
      .catch(() => {});
  }, []);

  // Rank bị tắt (toàn bộ) -> /api/ranks rỗng -> ẨN HẲN widget ở màn nạp tiền, KHÔNG thông báo gì.
  // (Thông báo lý do tạm ngừng chỉ hiển thị ở màn /membership khi user chủ động vào.)
  if (ranks.length === 0) {
    return null;
  }

  // Hạng user đang giữ chỉ "hợp lệ" nếu còn nằm trong danh sách hạng ĐANG BẬT (server đã lọc).
  // Nếu rank_id cũ đã bị tắt -> coi như chưa có hạng (không tô sáng, không tính mốc).
  const effectiveRank = currentRank ? ranks.find(r => r.id === currentRank.id) || null : null;

  const currentIdx = effectiveRank ? ranks.findIndex(r => r.id === effectiveRank.id) : -1;
  const nextRank = currentIdx >= 0 && currentIdx < ranks.length - 1 ? ranks[currentIdx + 1] : currentIdx < 0 ? ranks[0] : null;
  const currentMinAmount = effectiveRank?.minAmount || 0;
  const nextMinAmount = nextRank?.minAmount || currentMinAmount;
  const walletBalance = account?.wallet || 0;

  // Progress to next rank (simplified: based on wallet balance vs next rank requirement)
  const progressPercent = nextRank
    ? Math.min(100, Math.max(0, ((walletBalance - currentMinAmount) / (nextMinAmount - currentMinAmount)) * 100))
    : 100;

  return (
    <div className="ms-card" style={{ padding: 20, marginBottom: 20 }}>
      <style>{`
        @keyframes rankWidgetGlow {
          0%, 100% { box-shadow: 0 0 12px rgba(245,158,11,0.15); }
          50% { box-shadow: 0 0 24px rgba(245,158,11,0.3); }
        }
        @keyframes rankProgressShine {
          0% { left: -100%; }
          100% { left: 200%; }
        }
      `}</style>

      {/* Current rank display */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 16 }}>
        <div
          style={{
            width: 52,
            height: 52,
            borderRadius: 14,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: 26,
            background: `linear-gradient(135deg, ${RANK_COLORS[Math.min(currentIdx + 1, RANK_COLORS.length - 1)]}22, ${RANK_COLORS[Math.min(currentIdx + 1, RANK_COLORS.length - 1)]}08)`,
            border: `2px solid ${RANK_COLORS[Math.min(currentIdx + 1, RANK_COLORS.length - 1)]}44`,
            animation: 'rankWidgetGlow 3s ease-in-out infinite',
          }}
        >
          {RANK_ICONS[Math.min(currentIdx + 1, RANK_ICONS.length - 1)]}
        </div>
        <div style={{ flex: 1 }}>
          <div
            style={{
              fontSize: 11,
              color: colors.dim,
              textTransform: 'uppercase',
              letterSpacing: '.08em',
              fontWeight: 600,
              marginBottom: 2,
            }}
          >
            {t('depositWidget.currentRank')}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontSize: 18, fontWeight: 800, color: RANK_COLORS[Math.min(currentIdx + 1, RANK_COLORS.length - 1)] }}>
              {effectiveRank?.name || 'Member'}
            </span>
            {effectiveRank?.discountPercent > 0 && (
              <span
                style={{
                  padding: '2px 8px',
                  borderRadius: 6,
                  fontSize: 11,
                  fontWeight: 700,
                  background: colors.successDim,
                  color: colors.success,
                  fontFamily: "'JetBrains Mono', monospace",
                }}
              >
                -{effectiveRank.discountPercent}%
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Progress to next rank */}
      {nextRank ? (
        <div style={{ marginBottom: 14 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
            <span style={{ fontSize: 12, color: colors.muted }}>
              {t('depositWidget.progressTo')}{' '}
              <strong style={{ color: RANK_COLORS[Math.min(currentIdx + 2, RANK_COLORS.length - 1)] }}>{nextRank.name}</strong>
            </span>
            <span style={{ fontSize: 11, color: colors.dim, fontFamily: "'JetBrains Mono', monospace" }}>{formatVND(nextMinAmount)}</span>
          </div>
          {/* Progress bar */}
          <div
            style={{
              height: 8,
              borderRadius: 4,
              background: colors.surface,
              overflow: 'hidden',
              position: 'relative',
            }}
          >
            <div
              style={{
                height: '100%',
                borderRadius: 4,
                background: `linear-gradient(90deg, ${RANK_COLORS[Math.min(currentIdx + 1, RANK_COLORS.length - 1)]}, ${RANK_COLORS[Math.min(currentIdx + 2, RANK_COLORS.length - 1)]})`,
                width: `${progressPercent}%`,
                transition: 'width 0.5s ease',
                position: 'relative',
                overflow: 'hidden',
              }}
            >
              <div
                style={{
                  position: 'absolute',
                  top: 0,
                  width: '50%',
                  height: '100%',
                  background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.3), transparent)',
                  animation: 'rankProgressShine 2s ease-in-out infinite',
                }}
              />
            </div>
          </div>
          <div style={{ marginTop: 6, fontSize: 11, color: colors.dim }}>
            {t('depositWidget.depositMore')}{' '}
            <strong style={{ color: colors.accent, fontFamily: "'JetBrains Mono', monospace" }}>
              {formatVND(Math.max(0, nextMinAmount - walletBalance))}
            </strong>{' '}
            {t('depositWidget.toUpgrade')} <strong style={{ color: colors.success }}>{nextRank.discountPercent}%</strong>{' '}
            {t('depositWidget.allOrders')}
          </div>
        </div>
      ) : (
        <div
          style={{
            padding: '10px 14px',
            borderRadius: 8,
            background: 'linear-gradient(135deg, rgba(245,158,11,0.1), rgba(249,115,22,0.05))',
            border: `1px solid ${colors.accent}22`,
            fontSize: 12,
            color: colors.accent,
            fontWeight: 600,
            textAlign: 'center',
          }}
        >
          {t('depositWidget.highestRank')} {effectiveRank?.discountPercent}% {t('depositWidget.allOrders')}
        </div>
      )}

      {/* Mini rank tiers */}
      <div style={{ display: 'flex', gap: 4, marginTop: 12 }}>
        {ranks.map((rank, idx) => {
          const isPast = currentIdx >= idx;
          const isCurrent = currentIdx === idx;
          const isNext = currentIdx + 1 === idx;
          return (
            <div
              key={rank.id}
              style={{
                flex: 1,
                padding: '8px 4px',
                borderRadius: 8,
                textAlign: 'center',
                background: isCurrent ? `${RANK_COLORS[idx + 1]}15` : isNext ? `${RANK_COLORS[idx + 1]}08` : colors.surface,
                border: `1px solid ${isCurrent ? RANK_COLORS[idx + 1] + '55' : isNext ? RANK_COLORS[idx + 1] + '22' : colors.border}`,
                opacity: isPast && !isCurrent ? 0.5 : 1,
                transition: 'all .2s',
              }}
            >
              <div style={{ fontSize: 16, marginBottom: 2 }}>{RANK_ICONS[Math.min(idx + 1, RANK_ICONS.length - 1)]}</div>
              <div style={{ fontSize: 9, fontWeight: 700, color: RANK_COLORS[Math.min(idx + 1, RANK_COLORS.length - 1)], marginBottom: 1 }}>
                {rank.name}
              </div>
              <div style={{ fontSize: 9, color: colors.dim, fontFamily: "'JetBrains Mono', monospace" }}>-{rank.discountPercent}%</div>
            </div>
          );
        })}
      </div>

      <Link
        to="/membership"
        style={{
          display: 'block',
          textAlign: 'center',
          marginTop: 12,
          fontSize: 12,
          fontWeight: 600,
          color: colors.accent,
          textDecoration: 'none',
        }}
      >
        {t('depositWidget.viewDetails')}
      </Link>
    </div>
  );
};
